import {
  PRIORITIES,
  PRIORITY_META,
  SORT_FIELDS,
  STATUS_META,
  TASK_STATUSES,
  TASK_TYPES,
  TASK_TYPE_META,
  type SortField,
  type TaskListQuery,
} from '@taskos/shared';
import { ArrowDownNarrowWide, ArrowUpNarrowWide, Filter, X } from 'lucide-react';
import { useState } from 'react';
import { useCategories, useDirectory, useProjects } from '../../api/queries';
import { Button, Field, Modal, Toggle } from '../../components/ui';

export type Filters = Partial<Pick<TaskListQuery, 'priority' | 'status' | 'taskType' | 'visibility' | 'assigneeId' | 'createdById' | 'projectId' | 'categoryId' | 'tag' | 'dueFrom' | 'dueTo' | 'createdFrom' | 'createdTo' | 'includeClosed'>>;

export const SORT_LABEL: Record<SortField, string> = { priority: 'Priority', due: 'Due date', created: 'Created', updated: 'Updated', assignee: 'Assignee', title: 'Title' };

export function countFilters(f: Filters): number {
  return Object.values(f).filter((v) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== '' && v !== false)).length;
}

function toggleIn<T extends string>(arr: T[] | undefined, v: T): T[] {
  const s = new Set(arr ?? []);
  if (s.has(v)) s.delete(v);
  else s.add(v);
  return [...s];
}

export function SortControl({ sort, dir, onChange }: { sort: SortField; dir: 'asc' | 'desc'; onChange: (s: SortField, d: 'asc' | 'desc') => void }) {
  return (
    <div className="inline-row" style={{ gap: 4 }}>
      <label className="sr-only" htmlFor="sort-field">
        Sort by
      </label>
      <select id="sort-field" className="select" style={{ width: 140 }} value={sort} onChange={(e) => onChange(e.target.value as SortField, dir)}>
        {SORT_FIELDS.map((s) => (
          <option key={s} value={s}>
            Sort: {SORT_LABEL[s]}
          </option>
        ))}
      </select>
      <Button iconOnly icon={dir === 'asc' ? <ArrowUpNarrowWide size={16} /> : <ArrowDownNarrowWide size={16} />} aria-label={`Sort ${dir === 'asc' ? 'ascending' : 'descending'} — click to reverse`} onClick={() => onChange(sort, dir === 'asc' ? 'desc' : 'asc')} />
    </div>
  );
}

/** Removable chips summarising active filters. */
export function ActiveFilterChips({ f, onChange }: { f: Filters; onChange: (f: Filters) => void }) {
  const dir = useDirectory();
  const projects = useProjects();
  const cats = useCategories();
  const name = (id: string) => (id === 'none' ? 'Unassigned' : (dir.data?.find((u) => u.id === id)?.name ?? '…'));
  const chips: Array<{ label: string; remove: () => void }> = [];
  f.priority?.forEach((p) => chips.push({ label: `${PRIORITY_META[p].emoji} ${PRIORITY_META[p].label}`, remove: () => onChange({ ...f, priority: f.priority!.filter((x) => x !== p) }) }));
  f.status?.forEach((s) => chips.push({ label: STATUS_META[s].label, remove: () => onChange({ ...f, status: f.status!.filter((x) => x !== s) }) }));
  f.taskType?.forEach((t) => chips.push({ label: TASK_TYPE_META[t].label, remove: () => onChange({ ...f, taskType: f.taskType!.filter((x) => x !== t) }) }));
  f.visibility?.forEach((v) => chips.push({ label: v.toLowerCase(), remove: () => onChange({ ...f, visibility: f.visibility!.filter((x) => x !== v) }) }));
  f.assigneeId?.forEach((a) => chips.push({ label: `Assignee: ${name(a)}`, remove: () => onChange({ ...f, assigneeId: f.assigneeId!.filter((x) => x !== a) }) }));
  f.createdById?.forEach((a) => chips.push({ label: `Created by: ${name(a)}`, remove: () => onChange({ ...f, createdById: f.createdById!.filter((x) => x !== a) }) }));
  f.projectId?.forEach((p) => chips.push({ label: `Project: ${projects.data?.find((x) => x.id === p)?.name ?? '…'}`, remove: () => onChange({ ...f, projectId: f.projectId!.filter((x) => x !== p) }) }));
  f.categoryId?.forEach((p) => chips.push({ label: `Category: ${cats.data?.find((x) => x.id === p)?.name ?? '…'}`, remove: () => onChange({ ...f, categoryId: f.categoryId!.filter((x) => x !== p) }) }));
  f.tag?.forEach((t) => chips.push({ label: `#${t}`, remove: () => onChange({ ...f, tag: f.tag!.filter((x) => x !== t) }) }));
  if (f.dueFrom || f.dueTo) chips.push({ label: `Due ${f.dueFrom ?? '…'} → ${f.dueTo ?? '…'}`, remove: () => onChange({ ...f, dueFrom: undefined, dueTo: undefined }) });
  if (f.createdFrom || f.createdTo) chips.push({ label: `Created ${f.createdFrom ?? '…'} → ${f.createdTo ?? '…'}`, remove: () => onChange({ ...f, createdFrom: undefined, createdTo: undefined }) });
  if (f.includeClosed) chips.push({ label: 'Including closed', remove: () => onChange({ ...f, includeClosed: undefined }) });
  if (!chips.length) return null;
  return (
    <div className="chip-row mb" aria-label="Active filters">
      {chips.map((c) => (
        <button key={c.label} type="button" className="chip active" onClick={c.remove} aria-label={`Remove filter ${c.label}`}>
          {c.label} <X size={14} aria-hidden />
        </button>
      ))}
      <button type="button" className="link-btn small" onClick={() => onChange({})}>
        Clear all
      </button>
    </div>
  );
}

export function FilterButton({ f, onChange }: { f: Filters; onChange: (f: Filters) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Filters>(f);
  const dir = useDirectory();
  const projects = useProjects();
  const cats = useCategories();
  const [tag, setTag] = useState('');
  const n = countFilters(f);
  const set = (patch: Filters) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <>
      <Button
        icon={<Filter size={16} />}
        onClick={() => {
          setDraft(f);
          setOpen(true);
        }}
        aria-label={`Filters${n ? `, ${n} active` : ''}`}
      >
        Filters{n ? ` (${n})` : ''}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Filters"
        footer={
          <>
            <Button onClick={() => setDraft({})}>Reset</Button>
            <Button
              variant="primary"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="stack sm">
            <span className="label">Priority</span>
            <div className="chip-row">
              {PRIORITIES.map((p) => (
                <button key={p} type="button" className="chip" aria-pressed={draft.priority?.includes(p) ?? false} onClick={() => set({ priority: toggleIn(draft.priority, p) })}>
                  {PRIORITY_META[p].emoji} {PRIORITY_META[p].label}
                </button>
              ))}
            </div>
          </div>
          <div className="stack sm">
            <span className="label">Status</span>
            <div className="chip-row">
              {TASK_STATUSES.map((s) => (
                <button key={s} type="button" className="chip" aria-pressed={draft.status?.includes(s) ?? false} onClick={() => set({ status: toggleIn(draft.status, s) })}>
                  {STATUS_META[s].label}
                </button>
              ))}
            </div>
          </div>
          <div className="stack sm">
            <span className="label">Task type</span>
            <div className="chip-row">
              {TASK_TYPES.map((t) => (
                <button key={t} type="button" className="chip" aria-pressed={draft.taskType?.includes(t) ?? false} onClick={() => set({ taskType: toggleIn(draft.taskType, t) })}>
                  {TASK_TYPE_META[t].emoji} {TASK_TYPE_META[t].label}
                </button>
              ))}
            </div>
          </div>
          <div className="stack sm">
            <span className="label">Visibility</span>
            <div className="chip-row">
              {(['TEAM', 'PERSONAL', 'PRIVATE'] as const).map((v) => (
                <button key={v} type="button" className="chip" aria-pressed={draft.visibility?.includes(v) ?? false} onClick={() => set({ visibility: toggleIn(draft.visibility, v) })}>
                  {v === 'TEAM' ? '👥 Team' : v === 'PERSONAL' ? '🙋 Personal' : '🔒 Private'}
                </button>
              ))}
            </div>
          </div>
          <div className="form-grid">
            <Field label="Assigned to" htmlFor="f-assignee">
              <select id="f-assignee" className="select" value={draft.assigneeId?.[0] ?? ''} onChange={(e) => set({ assigneeId: e.target.value ? [e.target.value] : [] })}>
                <option value="">Anyone</option>
                <option value="none">Unassigned</option>
                {(dir.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Created by" htmlFor="f-creator">
              <select id="f-creator" className="select" value={draft.createdById?.[0] ?? ''} onChange={(e) => set({ createdById: e.target.value ? [e.target.value] : [] })}>
                <option value="">Anyone</option>
                {(dir.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Project" htmlFor="f-project">
              <select id="f-project" className="select" value={draft.projectId?.[0] ?? ''} onChange={(e) => set({ projectId: e.target.value ? [e.target.value] : [] })}>
                <option value="">Any</option>
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Category" htmlFor="f-category">
              <select id="f-category" className="select" value={draft.categoryId?.[0] ?? ''} onChange={(e) => set({ categoryId: e.target.value ? [e.target.value] : [] })}>
                <option value="">Any</option>
                {(cats.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Due from" htmlFor="f-due-from">
              <input id="f-due-from" type="date" className="input" value={draft.dueFrom ?? ''} onChange={(e) => set({ dueFrom: e.target.value || undefined })} />
            </Field>
            <Field label="Due to" htmlFor="f-due-to">
              <input id="f-due-to" type="date" className="input" value={draft.dueTo ?? ''} min={draft.dueFrom} onChange={(e) => set({ dueTo: e.target.value || undefined })} />
            </Field>
            <Field label="Created from" htmlFor="f-cr-from">
              <input id="f-cr-from" type="date" className="input" value={draft.createdFrom ?? ''} onChange={(e) => set({ createdFrom: e.target.value || undefined })} />
            </Field>
            <Field label="Created to" htmlFor="f-cr-to">
              <input id="f-cr-to" type="date" className="input" value={draft.createdTo ?? ''} min={draft.createdFrom} onChange={(e) => set({ createdTo: e.target.value || undefined })} />
            </Field>
            <div className="field span-2">
              <label htmlFor="f-tag">Tags</label>
              <div className="chip-row">
                {(draft.tag ?? []).map((t) => (
                  <button key={t} type="button" className="chip active" onClick={() => set({ tag: draft.tag!.filter((x) => x !== t) })}>
                    #{t} <X size={14} aria-hidden />
                  </button>
                ))}
                <input
                  id="f-tag"
                  className="input"
                  style={{ width: 180 }}
                  placeholder="Tag + Enter"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && tag.trim()) {
                      e.preventDefault();
                      set({ tag: [...new Set([...(draft.tag ?? []), tag.trim().replace(/^#/, '')])] });
                      setTag('');
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <Toggle checked={!!draft.includeClosed} onChange={(v) => set({ includeClosed: v || undefined })} label="Include completed, cancelled & archived" />
        </div>
      </Modal>
    </>
  );
}
